import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Mail, KeyRound, Timer } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { toast } from "../components/Toast";
import ThemeToggle from "../components/ThemeToggle";
import { LOGOUT_REASON_KEY } from "../auth/sessionConstants";
import "./Login.css";

const loginSchema = z.object({
  email: z.string().trim().min(1, "Indique o e-mail.").email("Indique um e-mail válido."),
  password: z.string().min(1, "Indique a palavra-passe."),
  twofa: z.string().optional(),
});

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [needs2fa, setNeeds2fa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState("");

  // Porque é que a sessão terminou (ex.: o painel esteve fechado ou parado).
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem(LOGOUT_REASON_KEY);
      if (reason) {
        sessionStorage.removeItem(LOGOUT_REASON_KEY);
        toast(reason, "info");
      }
    } catch {
      /* ignore */
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      twofa: "",
    },
  });

  const emailField = register("email");
  const passwordField = register("password");

  async function onSubmit(data) {
    setServerError("");
    if (needs2fa && !String(data.twofa || "").trim()) {
      setError("twofa", { type: "manual", message: "Indique o código 2FA." });
      return;
    }
    try {
      const loggedUser = await login(data.email, data.password, data.twofa || undefined);
      toast("Sessão iniciada com sucesso.", "success");
      if (loggedUser?.isSuperAdmin) navigate(loggedUser.twofa_enabled ? "/plataforma" : "/seguranca");
      else navigate("/dashboard");
    } catch (err) {
      const resData = err.response?.data;
      if (resData?.requires2fa) {
        setNeeds2fa(true);
        setServerError(resData?.message || "Código 2FA necessário.");
      } else if (err.response?.status === 429) {
        setServerError(resData?.message || "Muitas tentativas. Tente novamente em alguns minutos.");
      } else {
        setServerError(resData?.message || "E-mail ou palavra-passe incorrectos.");
      }
    }
  }

  return (
    <div className="login-bg">
      <div className="login-shell" role="main">
        <div className="login-hero" aria-label="Apresentação do painel da igreja">
          <div className="hero-badge">PAINEL DA IGREJA</div>
          <h2>Gestão central da igreja</h2>
          <p>
            Reúna cultos, escalas, membros, relatórios e comunicação num único ambiente
            de trabalho para a liderança.
          </p>

          <div className="hero-features">
            <div className="feature-card">
              <div className="feature-title">Acesso protegido</div>
              <div className="feature-sub">Sessão com renovação segura e suporte a 2FA</div>
            </div>
            <div className="feature-card">
              <div className="feature-title">Operação centralizada</div>
              <div className="feature-sub">Agenda, finanças e acompanhamento no mesmo painel</div>
            </div>
          </div>

          <div className="hero-tags">
            <span>Relatórios</span>
            <span>Membresia</span>
            <span>Escalas</span>
          </div>
        </div>

        <div className="login-card glass" aria-label="Formulário de acesso">
          <div className="login-row" style={{ alignItems: "center" }}>
            <div className="login-brand" aria-label="Painel da igreja">
              <span className="dot" />
              Painel da igreja
            </div>
            <ThemeToggle />
          </div>
          <div className="login-meta">
            <span className="pill">Painel da liderança</span>
            <span className="pill pill-sec">Acesso seguro</span>
          </div>

          <div className="login-lock" aria-hidden="true">
            <Lock size={28} strokeWidth={2.25} />
          </div>
          <div className="login-title">Bem-vindo de volta</div>
          <div className="login-subtitle">Aceda ao painel moderno da igreja.</div>

          {serverError && <div className="alert-error">{serverError}</div>}

          <form className="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <label className="label" htmlFor="email">
              E-mail
            </label>
            <div className="input-group">
              <span className="input-icon" aria-hidden="true">
                <Mail size={18} strokeWidth={2} />
              </span>
              <input
                id="email"
                className={`input ${errors.email || serverError ? "has-error" : ""}`}
                type="email"
                autoComplete="email"
                autoFocus
                aria-label="E-mail"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "email-error" : undefined}
                {...emailField}
              />
            </div>
            {errors.email && (
              <div id="email-error" className="field-hint">
                {errors.email.message}
              </div>
            )}

            <label className="label" htmlFor="password">
              Palavra-passe
            </label>
            <div className="input-group">
              <span className="input-icon" aria-hidden="true">
                <KeyRound size={18} strokeWidth={2} />
              </span>
              <input
                id="password"
                className={`input ${errors.password || serverError ? "has-error" : ""}`}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                aria-label="Palavra-passe"
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? "password-error" : undefined}
                {...passwordField}
              />
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
              >
                {showPassword ? "Ocultar" : "Mostrar"}
              </button>
            </div>
            {errors.password && (
              <div id="password-error" className="field-hint">
                {errors.password.message}
              </div>
            )}

            {needs2fa && (
              <>
                <label className="label" htmlFor="twofa">
                  Código 2FA
                </label>
                <div className="input-group">
                  <span className="input-icon" aria-hidden="true">
                    <Timer size={18} strokeWidth={2} />
                  </span>
                  <input
                    id="twofa"
                    className={`input ${errors.twofa || serverError ? "has-error" : ""}`}
                    inputMode="numeric"
                    maxLength={12}
                    aria-label="Código 2FA"
                    aria-invalid={Boolean(errors.twofa)}
                    {...register("twofa")}
                  />
                </div>
                {errors.twofa && (
                  <div className="field-hint">{errors.twofa.message}</div>
                )}
              </>
            )}

            <div className="login-row">
              <Link className="link-muted" to="/recuperar-palavra-passe">
                Esqueci-me da palavra-passe
              </Link>
            </div>
            <div className="login-row" style={{ marginTop: -4 }}>
              <span className="label" aria-label="Dica de segurança">
                Recomendamos ativar 2FA para sessões prolongadas.
              </span>
            </div>

            <button
              className="btn-primary btn-gradient"
              type="submit"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? "Entrando..." : needs2fa ? "Validar 2FA" : "Entrar no painel →"}
            </button>
          </form>

          <div className="login-footer">
            <div style={{ marginBottom: 8 }}>
              {/* Carrega a página de novo: o site público tem o seu próprio HTML (app do site). */}
              <Link className="link-muted" to="/public" reloadDocument>
                Ver o site público da igreja →
              </Link>
            </div>
            Ao continuar, concorda com os nossos termos e a política de privacidade.
          </div>
        </div>
      </div>
    </div>
  );
}
